import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

const StrategyPerformanceTable = ({ performanceData = [] }) => {
  const theme = useTheme();

  // Helper functions for formatting
  const formatValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "-";
    return value.toFixed(2);
  };

  const getValueColor = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return theme.palette.text.primary;
    if (value > 0) return theme.palette.success.main;
    if (value < 0) return theme.palette.error.main;
    return theme.palette.text.primary;
  };

  const months = [
    { key: "jan", label: "JAN" },
    { key: "feb", label: "FEB" },
    { key: "mar", label: "MAR" },
    { key: "apr", label: "APR" },
    { key: "may", label: "MAY" },
    { key: "jun", label: "JUN" },
    { key: "jul", label: "JUL" },
    { key: "aug", label: "AUG" },
    { key: "sep", label: "SEP" },
    { key: "oct", label: "OCT" },
    { key: "nov", label: "NOV" },
    { key: "dec", label: "DEC" },
  ];

  if (!performanceData || performanceData.length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Monthly & Annual Performance 
          </Typography>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "70vh",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  YEAR
                </TableCell>
                {months.map((month) => (
                  <TableCell
                    key={month.key}
                    align="right"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      minWidth: 80,
                    }}
                  >
                    {month.label}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  TWR
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  SHARPE
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  SORTINO
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  LARGEST DRAWDOWN
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceData.map((row) => (
                <TableRow key={row.year}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {row.year}
                  </TableCell>
                  {months.map((month) => (
                    <TableCell
                      key={month.key}
                      align="right"
                      sx={{
                        color: getValueColor(row[month.key]),
                        fontWeight: 500,
                      }}
                    >
                      {formatValue(row[month.key])}
                      {row[month.key] !== null && row[month.key] !== undefined && "%"}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      color: getValueColor(row.twr),
                      fontWeight: 500,
                      borderLeft: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    {formatValue(row.twr)}
                    {row.twr !== null && row.twr !== undefined && "%"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: getValueColor(row.sharpe),
                      fontWeight: 500,
                    }}
                  >
                    {formatValue(row.sharpe)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: getValueColor(row.sortino),
                      fontWeight: 500,
                    }}
                  >
                    {formatValue(row.sortino)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: getValueColor(row.largestDrawdown),
                      fontWeight: 500,
                    }}
                  >
                    {formatValue(row.largestDrawdown)}
                    {row.largestDrawdown !== null && row.largestDrawdown !== undefined && "%"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default StrategyPerformanceTable;

